import { ComponentType, useMemo } from "react";
import { ComboboxData, Select, TextInput } from "@mantine/core";
import { AppealSupportingDocumentType, ButtonSlotProps } from "../types";
import { documentTypeDisplayValueMap } from "../document-type-display-values";

import styles from "./choose-document-type-view.module.scss";

const emojiRegex = new RegExp(
  "([\\u2700-\\u27BF]|[\\uE000-\\uF8FF]|[\\uD83C-\\uDBFF\\uDC00-\\uDFFF]|[\\u2011-\\u26FF]|\\uFE0F)",
  "gu",
);

type ChooseDocumentTypeViewProps = {
  selectedDocumentType: AppealSupportingDocumentType | null;
  setSelectedDocumentType: (documentType: AppealSupportingDocumentType) => void;
  otherDocumentName: string;
  setOtherDocumentName: (documentName: string) => void;
  onContinue: () => void;
  excludedDocumentTypes: AppealSupportingDocumentType[];
  Button: ComponentType<ButtonSlotProps>;
};

export const ChooseDocumentTypeView = ({
  selectedDocumentType,
  setSelectedDocumentType,
  otherDocumentName,
  setOtherDocumentName,
  onContinue,
  excludedDocumentTypes,
  Button,
}: ChooseDocumentTypeViewProps) => {
  const documentTypeOptions = useMemo<ComboboxData>(() => {
    const excluded = new Set(excludedDocumentTypes);

    return Object.entries(documentTypeDisplayValueMap)
      .map(([key, value]) => ({ value: key, label: value }))
      .filter(
        ({ value }) => !excluded.has(value as AppealSupportingDocumentType),
      );
  }, [excludedDocumentTypes]);

  return (
    <>
      <h2 className={styles.modalContentHeader}>Tell us about your document</h2>
      <p className={styles.modalContentDescription}>
        Let us know what type of document you are uploading
      </p>
      <div className={styles.documentTypeSelects}>
        <Select
          label="What document are you uploading?"
          data={documentTypeOptions}
          placeholder="Select a document type"
          withCheckIcon={false}
          value={selectedDocumentType}
          onChange={(value) =>
            value &&
            setSelectedDocumentType(value as AppealSupportingDocumentType)
          }
          classNames={{
            label: styles.documentTypeLabel,
            input: styles.documentTypeInput,
            option: styles.documentTypeOption,
          }}
        />
        {selectedDocumentType === AppealSupportingDocumentType.Other && (
          <TextInput
            label="What should we call your document?"
            placeholder="Enter document name"
            classNames={{
              label: styles.documentTypeLabel,
              input: styles.documentTypeInput,
            }}
            value={otherDocumentName}
            onChange={(event) => {
              const valueWithoutForbiddenCharacters =
                event.currentTarget.value.replace(emojiRegex, "");

              setOtherDocumentName(valueWithoutForbiddenCharacters);
            }}
          />
        )}
      </div>
      <Button
        onClick={onContinue}
        type="button"
        variant="primary"
        size="medium"
        disabled={
          !selectedDocumentType ||
          (selectedDocumentType === AppealSupportingDocumentType.Other &&
            !otherDocumentName)
        }
        isFullWidth
      >
        Next
      </Button>
    </>
  );
};
